import { useEffect, useRef } from 'react';

import { useStateMachineInput } from '@rive-app/react-canvas';
import classNames from 'classnames';
import { observer } from 'mobx-react';

import { RIVE_DEFAULTS } from '../../../../hooks/rive/constants';
import { useRiveTextUpdater } from '../../../../hooks/rive/useRiveTextUpdater';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { type Banner } from '../../../../services/ui';

import styles from './RiveHeader.module.scss';
import { useRiveBannersAnimation } from './useRiveBannersAnimation';
import { getRiveBackgroundUrl, getRiveUrl } from './utils';

interface RiveHeaderProps {
  bannerCC: Banner;
  bannerSC: Banner;
  animationOnly?: Banner | null;
  currentCoinType: string;
}

const RiveHeader: React.FC<RiveHeaderProps> = observer(({ bannerCC, bannerSC, animationOnly, currentCoinType }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const riveFileUrlGC = getRiveUrl(bannerCC, isMobile);
  const riveFileUrlSC = getRiveUrl(bannerSC, isMobile);
  const riveFileUrlAnimationOnly = animationOnly ? getRiveUrl(animationOnly, isMobile) : '';
  const currentCoinTypeBanner = currentCoinType === 'GC' ? bannerCC : bannerSC;
  const bannerBackgroundUrl = getRiveBackgroundUrl(currentCoinTypeBanner, isMobile);

  const ref = useRef<HTMLDivElement>(null);

  const {
    showRive: showRiveAnimationOnly,
    RiveComponent: RiveComponentAnimationOnly,
    rive: riveAnimationOnly,
  } = useRiveBannersAnimation({
    riveFileUrl: riveFileUrlAnimationOnly,
  });

  const {
    showRive: showRiveGC,
    RiveComponent: RiveComponentGC,
    rive: riveGC,
  } = useRiveBannersAnimation({
    riveFileUrl: riveFileUrlGC,
  });

  const {
    showRive: showRiveSC,
    RiveComponent: RiveComponentSC,
    rive: riveSC,
  } = useRiveBannersAnimation({
    riveFileUrl: riveFileUrlSC,
  });

  useEffect(() => {
    if (!ref.current || !riveAnimationOnly) return;

    const ob = new IntersectionObserver(([entry]) => {
      if (!riveAnimationOnly) return;
      if (entry.isIntersecting) {
        riveAnimationOnly.play();
      } else {
        riveAnimationOnly.pause();
      }
    });

    ob.observe(ref.current);
    return () => ob.disconnect();
  }, [ref, riveAnimationOnly]);

  const isAnimationOnlyActive = showRiveAnimationOnly;
  const isGCPlaying = !isAnimationOnlyActive && currentCoinType === 'GC' && showRiveGC;
  const isSCPlaying = !isAnimationOnlyActive && currentCoinType === 'SC' && showRiveSC;

  const isScInput = useStateMachineInput(riveAnimationOnly ?? null, RIVE_DEFAULTS.STATE_MACHINE_NAME, 'is_sc', true);

  useEffect(() => {
    if (isAnimationOnlyActive && isScInput) {
      isScInput.value = currentCoinType === 'SC';
    }
  }, [isAnimationOnlyActive, isScInput, currentCoinType]);

  useRiveTextUpdater({
    rive: isAnimationOnlyActive ? null : riveGC,
    isRivePlaying: isGCPlaying,
  });

  useRiveTextUpdater({
    rive: isAnimationOnlyActive ? null : riveSC,
    isRivePlaying: isSCPlaying,
  });

  useRiveTextUpdater({
    rive: isAnimationOnlyActive ? riveAnimationOnly : null,
    isRivePlaying: isAnimationOnlyActive,
  });

  const hasAnyOverlay = showRiveGC || showRiveSC || showRiveAnimationOnly;

  return (
    <>
      <img alt="CrownCoinsCasino header" className={styles.banner} src={bannerBackgroundUrl} />
      {hasAnyOverlay && (
        <div ref={ref} className={styles.riveOverlay}>
          {isAnimationOnlyActive && (
            <RiveComponentAnimationOnly className={classNames(styles.animationContainer, styles.activeAnimation)} />
          )}

          {showRiveGC && !isAnimationOnlyActive && (
            <RiveComponentGC
              className={classNames(styles.animationContainer, {
                [styles.activeAnimation]: isGCPlaying,
                [styles.inactiveAnimation]: !isGCPlaying,
              })}
            />
          )}

          {showRiveSC && !isAnimationOnlyActive && (
            <RiveComponentSC
              className={classNames(styles.animationContainer, {
                [styles.activeAnimation]: isSCPlaying,
                [styles.inactiveAnimation]: !isSCPlaying,
              })}
            />
          )}
        </div>
      )}
    </>
  );
});

export default RiveHeader;
